import { Box, Button, Divider, Typography } from "@mui/material";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { FormattedMessage } from "react-intl";
import { useState } from "react";
import { useConstants } from "../hooks/UseConstants";
import AdminCourseFilePreview from "../components/course/AdminCourseFilePreview";
import AlertDialog from "../components/DialogView";
import {
    DetailMediaPanel,
    DetailModalShell,
    DetailSummaryCard,
} from "../components/admin/AdminDetailParts";

function CourseDetails({ data, onClickClose, onDeleted }) {
    const { language } = useConstants();
    const isAr = language === "ar";
    const contents = data.course?.contents?.filter((content) => content.content_type === "CourseFile") || [];
    const course = data?.course;
    const path = data?.path;
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const statusMeta = {
        accepted: { label: isAr ? "مقبولة" : "Approved", color: "success" },
        pending: { label: isAr ? "بانتظار الموافقة" : "Pending approval", color: "warning" },
        rejected: { label: isAr ? "مرفوضة" : "Rejected", color: "error" },
    }[course?.status] || { label: course?.status || "-", color: "default" };

    const formatDate = (value) => {
        if (!value) return isAr ? "غير متوفر" : "Unavailable";
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return value;
        return new Intl.DateTimeFormat(isAr ? "ar-EG" : "en-US", { dateStyle: "medium" }).format(parsed);
    };

    const courseName = language === "en" ? course?.name_en : course?.name_ar;
    const teacherName = course?.teacher
        ? `${course.teacher.first_name || ""} ${course.teacher.last_name || ""}`.trim()
        : "-";
    const categoryName = language === "en" ? course?.category?.name_en : course?.category?.name_ar;
    const pathName = path ? (language === "en" ? path?.name_en : path?.name_ar) : null;
    const description = language === "en" ? course?.description_en : course?.description_ar;

    const badges = [
        { key: "status", label: statusMeta.label, color: statusMeta.color },
    ];
    if (pathName) {
        badges.push({
            key: "path",
            label: pathName,
            sx: { bgcolor: "#eff6ff", color: "#0D358C", fontWeight: 700 },
        });
    }

    return (
        <>
            <DetailModalShell
                isAr={isAr}
                icon={<MenuBookOutlinedIcon sx={{ fontSize: 32 }} />}
                title={<FormattedMessage id="course_details" />}
                subtitle={isAr ? "مراجعة بيانات الدورة والمحتوى المرفوع بشكل أوضح." : "Review course information and uploaded content with a clearer layout."}
                onClose={onClickClose}
                maxWidth={1180}
            >
                <DetailMediaPanel
                    image={course?.image}
                    fallbackIcon={<MenuBookOutlinedIcon sx={{ fontSize: 56, color: "#94a3b8" }} />}
                    title={courseName}
                    subtitle={teacherName}
                    description={description || (isAr ? "لا يوجد وصف مضاف لهذه الدورة." : "No description added for this course.")}
                    badges={badges}
                />

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" }, gap: 2, mb: 3 }}>
                    <DetailSummaryCard
                        icon={<MenuBookOutlinedIcon />}
                        label={<FormattedMessage id="course_name" />}
                        value={courseName || "-"}
                    />
                    <DetailSummaryCard
                        icon={<PersonOutlineOutlinedIcon />}
                        label={<FormattedMessage id="teacher_name" />}
                        value={teacherName}
                    />
                    <DetailSummaryCard
                        icon={<CategoryOutlinedIcon />}
                        label={<FormattedMessage id="category" />}
                        value={categoryName || "-"}
                    />
                    {pathName ? (
                        <DetailSummaryCard
                            icon={<RouteOutlinedIcon />}
                            label={<FormattedMessage id="education_path" />}
                            value={pathName}
                        />
                    ) : null}
                    <DetailSummaryCard
                        icon={<FolderOutlinedIcon />}
                        label={<FormattedMessage id="course_files_count" />}
                        value={contents.length}
                    />
                    <DetailSummaryCard
                        icon={<CalendarMonthOutlinedIcon />}
                        label={<FormattedMessage id="publication_date" />}
                        value={formatDate(course?.created_at)}
                    />
                </Box>

                <Divider sx={{ mb: 2.5 }} />

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <FolderOutlinedIcon sx={{ color: "#0D358C" }} />
                    <Typography variant="h6" fontWeight={800} sx={{ color: "#0D358C" }}>
                        <FormattedMessage id="uploaded_files" />
                    </Typography>
                </Box>

                {contents.length > 0 ? (
                    <AdminCourseFilePreview
                        contents={data.course?.contents || []}
                        language={language}
                        emptyHintAr="لا توجد ملفات مرفوعة ضمن هذه الدورة."
                        emptyHintEn="No uploaded files were found for this course."
                    />
                ) : (
                    <Box
                        sx={{
                            p: 4,
                            borderRadius: 3,
                            border: "1px dashed",
                            borderColor: "#cbd5e1",
                            textAlign: "center",
                            color: "text.secondary",
                            bgcolor: "#f8fafc",
                        }}
                    >
                        {isAr ? "لا توجد ملفات مرفوعة ضمن هذه الدورة." : "No uploaded files were found for this course."}
                    </Box>
                )}

                {course?.status === "accepted" ? (
                    <>
                        <Divider sx={{ my: 3 }} />
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteOutlineOutlinedIcon />}
                                onClick={() => setConfirmDelete(true)}
                            >
                                <FormattedMessage id="delete_course" />
                            </Button>
                        </Box>
                    </>
                ) : null}
            </DetailModalShell>

            <AlertDialog
                openDialog={confirmDelete}
                title={isAr ? "حذف الدورة" : "Delete course"}
                description={isAr
                    ? "هل أنت متأكد من حذف هذه الدورة؟ سيصل إشعار للمدرس بأن دورته حُذفت."
                    : "Are you sure you want to delete this course? The teacher will be notified."}
                wait={deleting}
                onCancel={() => setConfirmDelete(false)}
                onConfirm={async () => {
                    if (!course?.id || !onDeleted) return;
                    setDeleting(true);
                    await onDeleted(course.id);
                    setDeleting(false);
                    setConfirmDelete(false);
                }}
            />
        </>
    );
}

export default CourseDetails;
